/**
 * ALGAE AURA — THEME JAVASCRIPT
 * Handles: hamburger nav, ingredient accordion, cart drawer (Shopify AJAX Cart API)
 */
/* ═════════════════════════════════════════════════════════════════════
   FUNNEL ANALYTICS BRIDGE  —  window.aaTrack(name, tags)

   One fan-out point for every custom event the theme fires. The theme owns
   no analytics account of its own, so this deliberately targets whatever
   happens to be installed and stays silent when nothing is:

     • Microsoft Clarity — installed as an app embed. `event` makes the name
       selectable under Dashboard → Filters → Event; `set` writes custom tags,
       which is what lets you filter and watch the matching session replays.
     • GA4 / GTM — gtag() or dataLayer, for the case where Google is added
       directly to the theme or through a GTM app embed. A no-op until then.
     • Shopify.analytics.publish — the supported route into Shopify's web
       pixels. Shopify's own GA4 (Google & YouTube channel) runs in a sandboxed
       pixel that cannot see window.gtag or a document event, so this is the
       ONLY hook that reaches it: a custom pixel calls
       analytics.subscribe('aa_quiz', …) and forwards to whatever it likes.
     • document → 'aa:track' — same stream for anything running in the page.

   Tag keys are namespaced by the caller (quiz_step, quiz_result, …) and kept
   to a small fixed set on purpose: Clarity caps how many distinct custom-tag
   keys a project can hold, so callers must not mint keys per session.

   Every call is wrapped — a broken or blocked analytics script must never be
   able to take a funnel step down with it.
═════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  window.aaTrack = function (name, tags) {
    tags = tags || {};

    try {
      /* Clarity's loader installs a queueing stub synchronously, so this is
         callable before the real script has finished downloading. */
      if (typeof window.clarity === 'function') {
        Object.keys(tags).forEach(function (k) {
          window.clarity('set', k, String(tags[k]));
        });
        window.clarity('event', name);
      }
    } catch (e) {}

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, tags);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, tags));
      }
    } catch (e) {}

    try {
      /* Custom pixel events must travel under a single registered name with
         the detail in the payload — pixels subscribe by name, and one stable
         name means new steps never need the pixel re-published. */
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('aa_quiz', Object.assign({ name: name }, tags));
      }
    } catch (e) {}

    try {
      document.dispatchEvent(new CustomEvent('aa:track', {
        detail: { name: name, tags: tags }
      }));
    } catch (e) {}
  };
}());

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════
     HAMBURGER / MOBILE NAV
  ══════════════════════════════════════════════════════════════════════ */
  function initMobileNav() {
    const toggle  = document.querySelector('.menu-toggle');
    const mobileNav = document.getElementById('mobile-nav');
    if (!toggle || !mobileNav) return;

    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      mobileNav.classList.toggle('is-open', !isOpen);
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });

    /* Close when a link inside the nav is tapped */
    mobileNav.addEventListener('click', e => {
      if (e.target.tagName === 'A') closeMobileNav();
    });

    /* Close on Escape key */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeMobileNav();
    });

    function closeMobileNav() {
      toggle.setAttribute('aria-expanded', 'false');
      mobileNav.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }


  /* ══════════════════════════════════════════════════════════════════════
     INGREDIENT ACCORDIONS (shared — works on any [data-accordion] element)
  ══════════════════════════════════════════════════════════════════════ */
  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(accordion => {
      accordion.addEventListener('click', e => {
        const trigger = e.target.closest('.ingredient-trigger');
        if (!trigger) return;

        const drawer = trigger.closest('.ingredient-drawer, .ingredient-item');
        if (!drawer) return;
        const isActive = drawer.classList.contains('active');

        /* Close all siblings */
        accordion.querySelectorAll('.ingredient-drawer, .ingredient-item').forEach(item => {
          item.classList.remove('active');
          item.querySelector('.ingredient-trigger')?.setAttribute('aria-expanded', 'false');
          item.querySelector('.ingredient-panel')?.setAttribute('aria-hidden', 'true');
        });

        /* Open the clicked one (unless it was already open — toggle off) */
        if (!isActive) {
          drawer.classList.add('active');
          trigger.setAttribute('aria-expanded', 'true');
          drawer.querySelector('.ingredient-panel')?.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════════════
     SHOPIFY AJAX CART DRAWER
  ══════════════════════════════════════════════════════════════════════ */
  const cartDrawer    = document.getElementById('cart-drawer');
  const cartOverlay   = document.getElementById('cart-overlay');
  const cartItemsEl   = document.getElementById('cart-items-container');
  const cartSubtotal  = document.getElementById('cart-subtotal');
  const cartBadges    = document.querySelectorAll('.bag-count-badge');

  let scrollLockY = 0;

  /* The pin is four inline styles on <body>. Anything that can leave them
     applied while the drawer is NOT open strands the shopper on a page that is
     covered by the overlay scrim and cannot be scrolled, with nothing on screen
     explaining why — so every exit path routes through here. */
  function releaseScrollLock() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
  }

  function openCart() {
    // iOS Safari ignores `overflow: hidden` on body, so pin the page in place
    // and restore the offset on close.
    //
    // Only pin if we are not already pinned. window.scrollY reads 0 once the
    // body is fixed, so re-running this on an open drawer would overwrite
    // scrollLockY with 0 and the next close would throw the shopper to the top
    // of the page. Reachable whenever a second add happens with the drawer up.
    if (!cartDrawer?.classList.contains('is-open')) {
      scrollLockY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollLockY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    cartDrawer?.classList.add('is-open');
    cartDrawer?.setAttribute('aria-hidden', 'false');
    cartOverlay?.classList.add('visible');
  }

  function closeCart() {
    /* Test the scrim as well as the drawer, never the drawer alone.
       #cart-overlay covers the viewport at z-index 1000 and takes
       pointer-events the moment it is .visible, so if it is ever up WITHOUT
       is-open on the drawer, guarding on is-open alone made this a no-op — the
       overlay then swallowed every tap, including the ones on itself, and the
       page was left permanently greyed over, unscrollable and unclickable with
       no way back. Any stray scrim must always be dismissible. */
    const drawerOpen = cartDrawer?.classList.contains('is-open');
    const scrimUp    = cartOverlay?.classList.contains('visible');
    if (!drawerOpen && !scrimUp) return;

    cartDrawer?.classList.remove('is-open');
    cartDrawer?.setAttribute('aria-hidden', 'true');
    cartOverlay?.classList.remove('visible');

    releaseScrollLock();
    window.scrollTo(0, scrollLockY);
  }

  function updateBadge(count) {
    cartBadges.forEach(badge => {
      badge.textContent = count > 99 ? '99+' : count;
      badge.classList.toggle('visible', count > 0);
    });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  /* ── Money formatting with graceful fallback ──────────────────────────── */
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  /* ── Escape product-supplied strings before innerHTML injection ────────── */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── Shopify CDN thumbnail — the cart slot renders at 64–84px, so never
        ship the 1200px+ master image (a real cost on mobile radios) ──────── */
  function thumb(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ── Editorial empty-state markup ─────────────────────────────────────── */
  function emptyStateHTML() {
    const gridUrl = (cartItemsEl && cartItemsEl.dataset.gridUrl) || '/collections/all';
    return `
      <div class="cart-empty">
        <p class="cart-empty-title">Your Bag is Empty</p>
        <p class="cart-empty-sub">Nothing has been added yet.</p>
        <a class="cart-empty-link" href="${gridUrl}">Explore the Collection</a>
      </div>`;
  }

  /* ── Line-item properties (e.g. bundle composition) → fine-line attribute list ── */
  function propertiesHTML(item) {
    const props = item.properties || {};
    const rows = Object.keys(props)
      .filter(key => key.charAt(0) !== '_' && props[key] !== '' && props[key] != null)
      .map(key => `
          <li class="cart-item-prop">
            <span class="cart-item-prop-key">${esc(key)}</span>
            <span class="cart-item-prop-val">${esc(props[key])}</span>
          </li>`)
      .join('');
    return rows ? `<ul class="cart-item-props">${rows}</ul>` : '';
  }

  /* ── What the line WOULD cost undiscounted. Line-level discounts surface as
        original_line_price > final_line_price; a Seal selling-plan price cut
        may instead surface via the allocation's compare_at_price. ─────────── */
  function lineOriginalCents(item) {
    if (item.original_line_price > item.final_line_price) return item.original_line_price;
    const alloc = item.selling_plan_allocation;
    if (alloc && alloc.compare_at_price && alloc.compare_at_price > alloc.price) {
      return alloc.compare_at_price * item.quantity;
    }
    return item.final_line_price;
  }

  /* ── Price fragment: struck original + discounted final when any discount
        (multi-product 22%, subscription 11%, …) is live on the line ───────── */
  function linePriceHTML(item) {
    const original = lineOriginalCents(item);
    if (original > item.final_line_price) {
      return `<span class="cart-price-was">${formatMoney(original)}</span> ` +
             `<span class="cart-price-now">${formatMoney(item.final_line_price)}</span>`;
    }
    return formatMoney(item.final_line_price);
  }

  /* ── Fine-print under the name: subscription plan and active discount names,
        so the shopper can SEE which rate each line is on ───────────────────── */
  function lineMetaText(item) {
    const bits = [];
    const alloc = item.selling_plan_allocation;
    if (alloc && alloc.selling_plan && alloc.selling_plan.name) {
      bits.push(esc(alloc.selling_plan.name));
    }
    (item.line_level_discount_allocations || []).forEach(a => {
      const app = a && a.discount_application;
      if (app && app.title) bits.push(esc(app.title));
    });
    return bits.join(' · ');
  }

  /* ── Single line-item markup ──────────────────────────────────────────── */
  function lineItemHTML(item) {
    const title = esc(item.product_title);
    const img = item.image
      ? `<img class="cart-item-img" src="${esc(thumb(item.image, 168))}" alt="${title}"
             width="72" height="72" loading="lazy" decoding="async">`
      : '<span class="cart-item-img" aria-hidden="true"></span>';
    return `
      <div class="cart-item" data-key="${item.key}">
        ${img}
        <div class="cart-item-details">
          <span class="cart-item-name">${title}</span>
          <span class="cart-item-plan" data-line-meta>${lineMetaText(item)}</span>
          ${propertiesHTML(item)}
          <span class="cart-item-price" data-line-price>${linePriceHTML(item)}</span>
          <div class="cart-item-qty">
            <button class="cart-qty-btn" data-action="dec" data-key="${item.key}" aria-label="Decrease quantity">−</button>
            <span class="cart-qty-val">${item.quantity}</span>
            <button class="cart-qty-btn" data-action="inc" data-key="${item.key}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="cart-item-remove" data-action="remove" data-key="${item.key}" aria-label="Remove ${title}">Remove</button>
      </div>`;
  }

  /* ── DEPRECATED: custom bundle visual-masking engine ──────────────────────
     The Complete Algal Ritual now runs entirely on the "Bundler — Product
     Bundles" app, which writes true, discounted line prices into the cart and
     integrates natively with Seal Subscriptions. The legacy _bundle_group
     token, per-line price masking, grouped bundle cards, and bundle-aware
     subtotal have been removed: the drawer renders every line as a standard
     item at its real final_line_price and trusts cart.total_price verbatim. */

  /* ── Build the full drawer body — every line is a standard item ─────────── */
  function buildCartHTML(cart) {
    return cart.items.map(lineItemHTML).join('');
  }

  /* ── Bundle-container guard ────────────────────────────────────────────
     The Complete Algal Ritual "product" is only a host page for the Bundler
     app widget; the app adds the real component products to the cart. The
     container's own variant is $0 and must never be sold on its own — it was
     reaching checkout through quick-add forms and direct /cart/add URLs,
     producing shipping-only orders. Theme forms are now guarded, but the URL
     path can't be blocked client-side, so any container line found in the
     cart is silently removed before the cart is rendered. */
  const BUNDLE_CONTAINER_HANDLES = ['complete-algal-ritual'];

  async function purgeBundleContainerLines(cart) {
    const rogue = (cart.items || []).filter(
      item => BUNDLE_CONTAINER_HANDLES.includes(item.handle)
    );
    let next = cart;
    for (const item of rogue) {
      try { next = await changeCartItem(item.key, 0); }
      catch { /* leave the line; checkout still charges $0 + shipping only */ }
    }
    return next;
  }

  /* ── Full fetch + reconcile (initial open / add-to-cart) ──────────────── */
  async function renderCart() {
    try {
      let cart = await fetchCart();
      cart = await purgeBundleContainerLines(cart);
      applyCart(cart, true);
    } catch {
      /* Network hiccup — leave the existing UI untouched */
    }
  }

  /* ── Advertised discount ladder → live drawer messaging ──────────────────
     1 product on subscription : 11% off the first order (22% from order 2+)
     1 product one-time        : no discount
     2+ products               : the set ladder (theme setting "Set discount
                                 ladder", e.g. 2 items 15%), and from "Lowest-
                                 priced item free from this many items" (e.g.
                                 3) the cheapest item is free instead
     Dropping back to fewer products removes the rate — the banner flips back
     to the "add another product" nudge on the same render pass, so the
     shopper is never silently re-priced. */
  const cartSavingsRow  = document.getElementById('cart-savings-row');
  const cartSavingsEl   = document.getElementById('cart-savings');
  const cartDiscountEl  = document.getElementById('cart-discount-status');
  const cartProgEl      = document.getElementById('cart-progress');
  const cartProgTrack   = document.getElementById('cart-progress-track');
  const cartProgFill    = document.getElementById('cart-progress-fill');
  const cartProgFoot    = document.getElementById('cart-progress-foot');
  /* The three commercial numbers come from theme settings via data attributes —
     see the comment on that element in layout/theme.liquid. Each falls back to
     its schema default so a missing element or an unsaved setting can never
     render "NaN% off" or a bar measured against zero. */
  /* "2:15, 3:25" → [{ qty: 2, pct: 15 }, { qty: 3, pct: 25 }], sorted by qty.
     Anything unparsable is dropped rather than guessed at. */
  const OFFER_TIERS = String((cartProgEl && cartProgEl.dataset.offerTiers) || '2:22')
    .split(',')
    .map(t => t.split(':').map(n => parseInt(n, 10)))
    .filter(([q, p]) => q > 1 && p > 0)
    .map(([qty, pct]) => ({ qty, pct }))
    .sort((a, b) => a.qty - b.qty);
  const SHIP_LINE  = parseInt(
    (cartProgEl && cartProgEl.dataset.shipLine) || '6500', 10) || 6500;
  const FLAT_RATE  = parseInt(
    (cartProgEl && cartProgEl.dataset.flatRate) || '5', 10) || 5;
  /* Free shipping by item count (a Shopify automatic free-shipping discount
     with a minimum quantity). 0 = not configured, and then only the price
     threshold counts. */
  const FREE_SHIP_ITEMS = parseInt(
    (cartProgEl && cartProgEl.dataset.freeShipItems) || '0', 10) || 0;
  /* "3rd item free": from this many items the lowest-priced one is free (a
     Shopify buy-2-get-1 discount, one per order). 0 = off. When on, percent
     tiers only count below it. */
  const FREE_ITEM_AT = parseInt(
    (cartProgEl && cartProgEl.dataset.freeItemAt) || '0', 10) || 0;
  const PCT_TIERS = OFFER_TIERS.filter(t => !FREE_ITEM_AT || t.qty < FREE_ITEM_AT);

  function updateDiscountUI(cart) {
    if (cartSavingsRow && cartSavingsEl) {
      /* Selling-plan discounts hide inside original_line_price (Shopify reports
         the plan-adjusted price as "original"), so sum savings per line the same
         way the line rows compute their strikethrough, then add any cart-level
         remainder not already counted. */
      let saved = 0, lineDelta = 0;
      cart.items.forEach(item => {
        saved     += lineOriginalCents(item) - item.final_line_price;
        lineDelta += item.original_line_price - item.final_line_price;
      });
      const cartLevel = ((cart.original_total_price || cart.total_price) - cart.total_price) - lineDelta;
      if (cartLevel > 0) saved += cartLevel;
      cartSavingsRow.hidden = saved <= 0;
      if (saved > 0) cartSavingsEl.textContent = '−' + formatMoney(saved);
    }

    if (!cartDiscountEl) return;

    /* ── Free-shipping progress ───────────────────────────────────────────
       Measured against cart.total_price, the DISCOUNTED total, because that is
       the figure Shopify charges shipping against. Measuring the sticker total
       instead would show a full bar to a shopper checkout then bills $5 — the
       same "promising money they will not get" failure the discount rules below
       exist to avoid, pointed at shipping.

       The consequence is that the bar can move BACKWARD: at 22% off, clearing a
       $65 line needs $83.34 before the break, so any bag originating between
       those two figures has free shipping until the discount lands and loses it
       after. That is genuinely what happens to their order, so it is shown. The
       message names the saving alongside it, and the saving is always larger
       than the $5 — a 22% break big enough to cost the waiver is necessarily
       worth more than the waiver.

       Which prize leads when neither is won is decided per-cart, not by a fixed
       order. cheapestUnit — the least expensive single unit already in the bag —
       stands in for "what one more thing costs". If the shipping gap is smaller
       than that, shipping is the nearer prize and leads; otherwise the 22% does.

       What is deliberately NOT said anywhere below: that adding a product WILL
       clear the shipping line. It frequently will not — a $48 bag plus a $28.49
       product is $76.49, which the break takes to $59.66, still short. So every
       message states a present fact and a next action, never a compound promise
       about where the total lands. */

    /* DATA-DRIVEN, not rule-driven. Two different Bundler discounts can land
       here — "Quantity break" (2+ units of one product, added that way) and
       "Bundle" (two different products) — and neither is a clean function of
       cart contents that the theme can predict:

         PDP stepper set to 2, then Add to Bag  -> 22%  ("Quantity break")
         add product A, then add product B      -> 22%  ("Bundle")
         add product, then press + in THIS drawer -> no discount

       That third case is the trap. It is 2 units, so any count-based rule says
       "unlocked", but the app does not re-price a line it has already stamped
       and checkout charges full price. Predicting the discount therefore means
       occasionally promising money the shopper will not get, which is the one
       failure mode a bag message must never have. So: claim a discount only
       when the cart actually carries one, and otherwise nudge toward the path
       that reliably works (adding a different product). */
    const saved = (cart.original_total_price || cart.total_price) - cart.total_price;

    if (cart.item_count === 0) {
      cartDiscountEl.hidden = true;
      if (cartProgEl) cartProgEl.hidden = true;
      return;
    }
    cartDiscountEl.hidden = false;
    if (cartProgEl) cartProgEl.hidden = false;

    const total     = cart.total_price;
    const shipGap   = SHIP_LINE - total;
    const shipsByCount = FREE_SHIP_ITEMS > 0 && cart.item_count >= FREE_SHIP_ITEMS;
    const shipsFree = shipGap <= 0 || shipsByCount;
    const hasSub    = cart.items.some(i => i.selling_plan_allocation);

    /* Least expensive single unit in the bag — the stand-in for "what one more
       thing costs". Guards against quantity 0, which would divide to Infinity
       and make every gap look reachable. */
    let cheapestUnit = Infinity;
    cart.items.forEach(item => {
      const qty = item.quantity || 1;
      cheapestUnit = Math.min(cheapestUnit, item.final_line_price / qty);
    });

    /* The next rung of the set ladder — but only if it beats the rate the
       bag ALREADY carries, so the drawer never offers "20% off" to a bag that
       is sitting at more than that. Subscription bags get no rung: how the set
       rate meets a selling-plan discount is the apps' business, not a promise
       this line should make. The wording is "add another product", never
       "press + in the drawer": Bundler will not re-price a line it has already
       stamped. */
    const original = cart.original_total_price || cart.total_price;
    const pctNow = original > 0 ? (saved * 100) / original : 0;
    let nextTier = hasSub ? null
      : PCT_TIERS.find(t => t.qty > cart.item_count && t.pct > pctNow + 0.5);
    /* The free-item rung, when it's nearer than (or instead of) a percent one */
    const freeMore = (!hasSub && FREE_ITEM_AT > cart.item_count) ? FREE_ITEM_AT - cart.item_count : 0;
    if (freeMore && nextTier && nextTier.qty - cart.item_count >= freeMore) nextTier = null;
    let tierNudge = '';
    const plural = n => `<strong>${n} more product${n > 1 ? 's' : ''}</strong>`;
    if (freeMore && !nextTier) {
      const withShip = !shipsFree && FREE_SHIP_ITEMS > 0 && FREE_ITEM_AT >= FREE_SHIP_ITEMS;
      tierNudge = `Add ${plural(freeMore)} and the <strong>lowest-priced one is free</strong>` +
                  `${withShip ? ', with free shipping' : ''}.`;
    } else if (nextTier) {
      const more = nextTier.qty - cart.item_count;
      const withShip = !shipsFree && FREE_SHIP_ITEMS > 0 && nextTier.qty >= FREE_SHIP_ITEMS;
      tierNudge = `Add ${plural(more)} and your whole bag becomes ` +
                  `<strong>${nextTier.pct}% off${withShip ? ' with free shipping' : ''}</strong>.`;
    } else if (!shipsFree && FREE_SHIP_ITEMS > cart.item_count) {
      tierNudge = `Add ${plural(FREE_SHIP_ITEMS - cart.item_count)} and <strong>shipping is free</strong>.`;
    }

    let head, foot;

    const freeItemOn = FREE_ITEM_AT > 0 && cart.item_count >= FREE_ITEM_AT && !hasSub;
    if (shipsFree && saved > 0) {
      head = `<strong>${freeItemOn ? 'Free item' : 'Set savings'} + free shipping</strong> — you saved ` +
             `${formatMoney(saved)} and the $${FLAT_RATE} rate is waived.`;
      foot = tierNudge;
    } else if (shipsFree) {
      head = `<strong>Free shipping unlocked</strong> — the $${FLAT_RATE} rate is waived on this bag.`;
      foot = tierNudge;
    } else if (saved > 0) {
      /* The backward case. Name the saving first and let the shipping gap follow
         as the smaller number, because that is the true ranking of the two. */
      const beatsShipping = saved > FLAT_RATE * 100;
      head = `<strong>${freeItemOn ? 'Free item applied' : 'Set savings applied'}</strong> — you saved ${formatMoney(saved)}` +
             (beatsShipping ? `, more than the $${FLAT_RATE} shipping.` : '.');
      foot = tierNudge || `${formatMoney(shipGap)} more and shipping is free too.`;
    } else if (shipGap <= cheapestUnit || !tierNudge) {
      head = `You're <strong>${formatMoney(shipGap)}</strong> from free shipping.`;
      foot = tierNudge;
    } else {
      head = tierNudge;
      foot = `Free shipping starts at ${formatMoney(SHIP_LINE)} — you're ${formatMoney(shipGap)} away.`;
    }

    cartDiscountEl.className = (saved > 0 || shipsFree) ? 'is-unlocked' : 'is-locked';
    cartDiscountEl.innerHTML = head;

    if (cartProgFoot) {
      cartProgFoot.hidden = !foot;
      if (foot) cartProgFoot.innerHTML = foot;
    }

    const pct = shipsFree ? 100 : Math.max(0, Math.min(100, Math.round((total / SHIP_LINE) * 100)));
    if (cartProgFill)  cartProgFill.style.width = pct + '%';
    if (cartProgTrack) {
      cartProgTrack.classList.toggle('is-complete', shipsFree);
      cartProgTrack.setAttribute('aria-valuenow', String(pct));
    }
  }

  /* ── Reconcile the DOM against a cart object (localized patch) ─────────── */
  function applyCart(cart, fullBuild) {
    updateBadge(cart.item_count);
    if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price);
    toggleClearTrigger(cart.item_count > 0);
    toggleCheckoutTrigger(cart.item_count > 0);
    updateDiscountUI(cart);

    if (!cartItemsEl) return;

    if (cart.item_count === 0) {
      cartItemsEl.innerHTML = emptyStateHTML();
      return;
    }

    if (fullBuild || !cartItemsEl.querySelector('.cart-item')) {
      cartItemsEl.innerHTML = buildCartHTML(cart);
      return;
    }

    /* Touch only the lines that actually moved. Prices AND discount labels are
       refreshed on every pass: adding/removing a product can re-price EVERY
       other line (a set tier appearing or falling away). */
    cart.items.forEach(item => {
      const row = cartItemsEl.querySelector(`.cart-item[data-key="${item.key}"]`);
      if (!row) {
        cartItemsEl.insertAdjacentHTML('beforeend', lineItemHTML(item));
        return;
      }
      const qtyEl   = row.querySelector('.cart-qty-val');
      const priceEl = row.querySelector('[data-line-price]');
      const metaEl  = row.querySelector('[data-line-meta]');
      if (qtyEl)   qtyEl.textContent   = item.quantity;
      if (priceEl) priceEl.innerHTML   = linePriceHTML(item);
      if (metaEl)  metaEl.innerHTML    = lineMetaText(item);
    });
  }

  /* ── /cart/change.js — defensive, returns the updated cart ────────────── */
  async function changeCartItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error('cart-change-failed');
    return res.json();
  }

  /* ── /cart/clear.js — flush the whole checkout session ────────────────── */
  async function clearCart() {
    const res = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error('cart-clear-failed');
    return res.json();
  }

  function toggleClearTrigger(show) {
    const btn = document.getElementById('cart-clear-btn');
    if (btn) btn.classList.toggle('is-visible', !!show);
  }

  /* Checkout is a dead end on an empty cart: GET /checkout bounces to /cart,
     and this theme ships no cart template, so the shopper lands on a 404.
     Mark the trigger from REAL cart data only (never the server-rendered
     badge) — an unmarked button always navigates, so an unknown cart state
     can't strand anyone. */
  function toggleCheckoutTrigger(hasItems) {
    const btn = document.getElementById('cart-checkout-btn');
    if (!btn) return;
    btn.classList.toggle('is-disabled', !hasItems);
    btn.setAttribute('aria-disabled', hasItems ? 'false' : 'true');
  }

  let cartErrorTimer;
  function showCartError(msg) {
    const el = document.getElementById('cart-error');
    if (!el) return;
    el.textContent = msg || 'Update unavailable — please try again.';
    el.hidden = false;
    clearTimeout(cartErrorTimer);
    cartErrorTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }

  /* Public add-to-cart used by section forms.
     Pass sellingPlanId when a Seal Subscriptions plan is active. */
  async function addToCart(variantId, quantity = 1, sellingPlanId = null) {
    try {
      const payload = { id: variantId, quantity };
      if (sellingPlanId) payload.selling_plan = parseInt(sellingPlanId, 10);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.description || 'Could not add to cart.');
        return;
      }
      await renderCart();
      openCart();
    } catch {
      alert('Something went wrong. Please try again.');
    }
  }

  /* ── Add several lines in ONE request ──────────────────────────────────
     Not a convenience wrapper. Verified against a live cart: posting two
     products as a single /cart/add.js request applies the Bundler discount,
     while posting them as two sequential requests can leave the bag at full
     price. The pairing offers quote an exact total, so they must take the path
     that actually produces it. */
  async function addItemsToCart(items) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        let msg = 'Could not add to cart.';
        try { msg = (await res.json()).description || msg; } catch { /* keep default */ }
        alert(msg);
        return false;
      }
      await renderCart();
      openCart();
      return true;
    } catch {
      alert('Something went wrong. Please try again.');
      return false;
    }
  }

  /* ── One-tap pairing offers (snippets/pair-offer-buttons.liquid) ────────
     Adds the page's product plus one add-on together. The main variant is read
     from the section's product form at click time rather than baked into the
     button, so a variant switch or a quantity change is always respected.

     No selling_plan is sent: the price on the button is one-time arithmetic,
     and the block is hidden outright while a subscription is selected (see
     initExpressCheckoutGuard), so this path is only ever reachable one-time. */
  function initPairOffers() {
    document.querySelectorAll('[data-pair-offer]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const section = btn.closest('[data-product-section]');
        const form    = section && section.querySelector('form[action="/cart/add"]');
        const idInput = form && form.querySelector('[name="id"]');
        if (!idInput || !idInput.value) return;

        const addonId = btn.dataset.addonVariant;
        if (!addonId) return;

        const qtyInput = form.querySelector('[name="quantity"]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        const original = btn.dataset.originalHTML || btn.innerHTML;
        btn.dataset.originalHTML = original;
        btn.disabled = true;
        btn.classList.add('is-busy');

        await addItemsToCart([
          { id: idInput.value, quantity: qty },
          { id: addonId,       quantity: 1 },
        ]);

        btn.disabled = false;
        btn.classList.remove('is-busy');
      });
    });
  }

  function initCart() {
    if (!cartDrawer) return;

    /* Close on overlay or close button */
    cartOverlay?.addEventListener('click', closeCart);
    document.getElementById('cart-close-btn')?.addEventListener('click', closeCart);

    /* ── Never navigate away with the drawer still open ────────────────────
       "Continue Shopping" and the empty-state link are plain <a>s living
       INSIDE the drawer, so tapping one used to leave the page pinned and the
       34% overlay scrim painted while the browser navigated. Two consequences,
       both of which read to a shopper as a broken site:

         • Those inline body styles and the .visible scrim get frozen into the
           back/forward-cache snapshot. Returning to the page restores it
           greyed over and unscrollable.
         • With cross-document view transitions enabled in the <head>, the
           OUTGOING snapshot the browser cross-fades from includes that scrim.

       Closing first costs nothing and removes the whole class of problem. We
       never preventDefault — the navigation still happens normally. */
    cartDrawer.addEventListener('click', e => {
      const link = e.target.closest('a[href]');
      if (!link || !cartDrawer.contains(link)) return;
      if (link.getAttribute('aria-disabled') === 'true') return;  /* empty-bag checkout */
      closeCart();
    });

    /* Belt and braces for the bfcache: if this page is restored from it, drop
       any lock and scrim that were captured with the snapshot, and resync —
       the cart may have changed in another tab or at checkout since. */
    window.addEventListener('pageshow', e => {
      if (!e.persisted) return;
      releaseScrollLock();
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
      cartOverlay?.classList.remove('visible');
      renderCart();
    });

    /* Checkout button → Shopify checkout.
       The anchor's href ({{ routes.checkout_url }}) can render EMPTY in some
       storefront contexts; an <a href=""> then navigates to the CURRENT page,
       which silently races out any scripted redirect. So we always cancel the
       default action and drive navigation explicitly to the checkout route.

       NEVER gate this on the badge. The badge holds a server-rendered count
       that is only corrected by updateBadge(), so any path that fills the cart
       without a drawer render leaves it reading "0" — and a guard keyed on that
       turns Proceed to Checkout into a button that does nothing at all, with no
       navigation and no error. Shopify already handles an empty cart itself. */
    document.getElementById('cart-checkout-btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      if (e.currentTarget.getAttribute('aria-disabled') === 'true') return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.href = root.replace(/\/$/, '') + '/checkout';
    });

    /* ── Section-dispatched cart events ────────────────────────────────────
       The landing-page buy cards and every quiz result card run their OWN
       fetch to /cart/add.js and then announce it with these two events rather
       than going through the form interceptor below (some of them are plain
       <button>s with no form at all; the Emerald buy-card form handler also
       calls stopPropagation, so the document-level interceptor never sees it).
       Nothing used to listen here, so those adds landed in the real cart while
       the drawer stayed shut and the badge kept its page-load value — the
       shopper got "Added!" for two seconds and no way onward.

       Both events are always dispatched together, so coalesce them into a
       single /cart.js round trip and open only once the fresh contents are in
       (renderCart swallows its own failures, so this always resolves). */
    let pendingSync = null;
    function syncCart() {
      if (!pendingSync) {
        pendingSync = renderCart().finally(() => { pendingSync = null; });
      }
      return pendingSync;
    }
    document.addEventListener('cart:refresh', () => { syncCart(); });
    document.addEventListener('theme:cart:open', () => { syncCart().then(openCart); });

    /* Escape key */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeCart();
    });

    /* Swipe right to dismiss (mobile). The drawer tracks the finger, then
       either snaps back or closes past a third of its width. Vertical drags
       are handed back to the scrolling item list. */
    let touchStartX = 0, touchStartY = 0, dragX = 0, isDragging = false, axisLocked = false;

    cartDrawer.addEventListener('touchstart', e => {
      if (!cartDrawer.classList.contains('is-open') || e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      dragX = 0;
      isDragging = true;
      axisLocked = false;
    }, { passive: true });

    cartDrawer.addEventListener('touchmove', e => {
      if (!isDragging) return;
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;

      if (!axisLocked) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx)) { isDragging = false; return; } // vertical → let it scroll
        axisLocked = true;
        cartDrawer.style.transition = 'none';
      }

      dragX = Math.max(0, dx);
      cartDrawer.style.transform = `translateX(${dragX}px)`;
    }, { passive: true });

    function endDrag() {
      if (!isDragging) return;
      isDragging = false;
      cartDrawer.style.transition = '';
      cartDrawer.style.transform = '';
      if (dragX > cartDrawer.offsetWidth / 3) closeCart();
      dragX = 0;
    }

    cartDrawer.addEventListener('touchend', endDrag);
    cartDrawer.addEventListener('touchcancel', endDrag);

    /* Open cart on bag icon click — render fresh state first */
    document.querySelectorAll('.bag-button').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        renderCart();
        openCart();
      });
    });

    /* Intercept all add-to-cart forms on the page */
    document.addEventListener('submit', async e => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;

      /* Shopify's dynamic checkout button lives INSIDE this form but drives its
         own accelerated-checkout flow. If it ever submits the form natively,
         hijacking that into an AJAX add-to-cart would silently swallow an
         express purchase — the buyer taps Apple Pay and lands in the drawer. */
      if (e.submitter &&
          e.submitter.closest('.shopify-payment-button, shopify-accelerated-checkout')) {
        return;
      }

      e.preventDefault();

      const variantInput      = form.querySelector('[name="id"]');
      const qtyInput          = form.querySelector('[name="quantity"]');
      const sellingPlanInput  = form.querySelector('[name="selling_plan"]:not([disabled])');
      if (!variantInput) return;

      const variantId     = variantInput.value;
      const qty           = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
      const sellingPlanId = sellingPlanInput ? sellingPlanInput.value || null : null;

      /* Visual feedback on the submit button */
      const btn = form.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = true;
        btn.dataset.originalText = btn.textContent;
        btn.textContent = 'Adding…';
      }

      await addToCart(variantId, qty, sellingPlanId);

      if (btn) {
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = btn.dataset.originalText;
          btn.disabled = false;
        }, 1800);
      }
    });

    /* ── Delegated stepper / remove with debounce + optimistic UI ──────── */
    const DEBOUNCE_MS = 280;
    const changeTimers = {};

    /* ── Stale line-key self-heal ──────────────────────────────────────────
       A cart line's key is variantId:hash-of-line-properties, so ANY app that
       writes properties to a line after the fact changes its key. Bundler does
       exactly that — it stamps `_bndlr` on each line a beat after add-to-cart,
       asynchronously, with no event we can hook. The drawer has already
       rendered by then, so every +/−/Remove button in it is holding a key the
       cart no longer recognises: /cart/change.js answers 400 "no valid id or
       line parameter" and the shopper gets "That quantity is unavailable right
       now" until they close and reopen the bag.

       So: on failure, re-resolve this row's key from a fresh cart by its line
       position (DOM order always mirrors cart order — every render builds from
       the server cart) and retry once. Matched on variant as well as position,
       because acting on the wrong line here would silently edit a different
       product, which is far worse than the error we are recovering from. */
    async function resolveFreshKey(row) {
      if (!row) return null;
      const rows = [...cartItemsEl.querySelectorAll('.cart-item')];
      const idx = rows.indexOf(row);
      if (idx < 0) return null;
      const oldKey = row.getAttribute('data-key') || '';
      const variant = oldKey.split(':')[0];
      let cart;
      try { cart = await fetchCart(); } catch { return null; }
      const item = (cart.items || [])[idx];
      if (!item || String(item.variant_id) !== variant) return null;
      return item.key;
    }

    function rekeyRow(row, key) {
      if (!row || !key) return;
      row.setAttribute('data-key', key);
      row.querySelectorAll('[data-key]').forEach(el => el.setAttribute('data-key', key));
    }

    function scheduleChange(key, qty, row) {
      clearTimeout(changeTimers[key]);
      changeTimers[key] = setTimeout(async () => {
        try {
          let cart;
          try {
            cart = await changeCartItem(key, qty);
          } catch (firstErr) {
            const fresh = await resolveFreshKey(row);
            if (!fresh || fresh === key) throw firstErr;
            rekeyRow(row, fresh);
            cart = await changeCartItem(fresh, qty);
          }
          if (qty === 0) {
            if (row) row.classList.add('removing');
            updateBadge(cart.item_count);
            if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price);
            toggleClearTrigger(cart.item_count > 0);
            setTimeout(() => {
              if (row) row.remove();
              applyCart(cart, false);
            }, 340);
          } else {
            applyCart(cart, false);
          }
        } catch {
          /* Inventory ceiling / network failure — resync from source of truth */
          if (row) {
            row.classList.remove('pending-remove');
            row.querySelectorAll('button').forEach(b => (b.disabled = false));
          }
          showCartError('That quantity is unavailable right now.');
          renderCart();
        }
      }, DEBOUNCE_MS);
    }

    cartItemsEl?.addEventListener('click', e => {
      const btn = e.target.closest('[data-action]');
      if (!btn || !cartItemsEl.contains(btn)) return;

      const row = btn.closest('.cart-item');
      if (!row) return;
      const key    = btn.dataset.key;
      const action = btn.dataset.action;
      const qtyEl  = row.querySelector('.cart-qty-val');
      const current = parseInt(qtyEl?.textContent || '1', 10);

      let next = current;
      if (action === 'inc')         next = current + 1;
      else if (action === 'dec')    next = Math.max(0, current - 1);
      else if (action === 'remove') next = 0;

      if (next === 0) {
        row.classList.add('pending-remove');
        row.querySelectorAll('button').forEach(b => (b.disabled = true));
      } else if (qtyEl) {
        qtyEl.textContent = next;   /* optimistic — server response reconciles */
      }
      scheduleChange(key, next, row);
    });

    /* ── Global Clear Bag ─────────────────────────────────────────────── */
    document.getElementById('cart-clear-btn')?.addEventListener('click', async () => {
      try {
        applyCart(await clearCart(), true);
      } catch {
        showCartError('Could not clear your bag.');
      }
    });

    /* Initial badge count on page load */
    fetchCart().then(cart => updateBadge(cart.item_count)).catch(() => {});
  }


  /* ══════════════════════════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════════════════════════ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ══════════════════════════════════════════════════════════════════════
     SEAL SUBSCRIPTIONS → FORM BRIDGE  (Bug 1 fix)
     Global auto-injection is OFF and the Seal widget renders OUTSIDE the
     product form, so the selected selling_plan never reached our /cart/add.js
     payload — standalone subscriptions silently checked out as one-time.
     This reads the user's choice from the Seal widget (wherever it sits in the
     section) and writes it into the form's hidden, enabled selling_plan input,
     which the submit handler at the top of initCart already reads. One bridge
     per [data-product-section]; covers all standalone product pages.
  ══════════════════════════════════════════════════════════════════════ */
  function initSealFormBridge() {
    /* On standalone product pages, ONLY the standalone subscription (11%→22%)
       may ever be submitted. The bundle plan (0%→33%, group bb57c9a7…, plan
       7796687088) is reserved for the Bundler app and is merely CSS-hidden on
       these pages — which still leaves it selectable/submittable. We exclude it
       outright so a hidden/defaulted bundle option can never ride to checkout. */
    const EXCLUDE_PLAN_IDS  = ['7796687088'];
    const EXCLUDE_GROUP_IDS = ['bb57c9a70af7b7e9186e6a85d5ce8a91d8af1423'];

    document.querySelectorAll('[data-product-section]').forEach(section => {
      const form = section.querySelector('form[action="/cart/add"]');
      const seal = section.querySelector('.sealsubs-target-element, [data-seal-widget-target]');
      if (!form || !seal) return;

      /* Guarantee one ENABLED hidden selling_plan input lives inside the form
         (the Liquid one ships disabled+empty, which the handler skips). */
      let planInput = form.querySelector('input[name="selling_plan"]');
      if (!planInput) {
        planInput = document.createElement('input');
        planInput.type = 'hidden';
        planInput.name = 'selling_plan';
        form.appendChild(planInput);
      }
      planInput.removeAttribute('disabled');
      planInput.removeAttribute('aria-hidden');

      /* Resolve the selected plan from the Seal widget.
         THE ONE-TIME → SUBSCRIPTION BUG LIVED HERE: the old reader walked every
         ':checked' element INCLUDING the frequency dropdown's <option>, which
         stays selected even while the One-time radio is checked (and <option>
         was exempt from the visibility test) — so its numeric plan id won and
         every add-to-cart was silently upgraded to a subscription.
         Now the radios/checkboxes are the sole authority on one-time vs
         subscribe; a <select> only refines WHICH plan, never WHETHER one is
         active, and is only consulted inside the checked toggle's own option
         container. */
      function isVisible(el) { return !!(el && el.offsetParent !== null); }
      function isPlanId(id) { return /^[1-9]\d*$/.test(id); }
      function isExcluded(planId, el) {
        if (EXCLUDE_PLAN_IDS.indexOf(String(planId)) !== -1) return true;
        const g = el && el.closest && el.closest('[data-selling-plan-group-id]');
        return !!(g && EXCLUDE_GROUP_IDS.indexOf(g.getAttribute('data-selling-plan-group-id')) !== -1);
      }
      function planIdFrom(el) {
        const raw = el.getAttribute('data-selling-plan-id')
                 || el.getAttribute('data-selling_plan')
                 || el.value || '';
        return String(raw).trim();
      }
      /* The frequency <select> living in the SAME option container as the
         checked toggle (one-time containers have none, so this can't leak a
         plan onto a one-time choice). */
      function planFromOwnSelect(toggle) {
        const scope = toggle.closest('[data-selling-plan-group-id]')
                   || toggle.closest('.sls-option-container')
                   || toggle.closest('fieldset')
                   || toggle.parentElement;
        if (!scope) return '';
        for (const sel of scope.querySelectorAll('select')) {
          if (!isVisible(sel) || sel.selectedIndex < 0) continue;
          const opt = sel.options[sel.selectedIndex];
          const id = String(opt.getAttribute('data-selling-plan-id') || opt.value || '').trim();
          if (isPlanId(id) && !isExcluded(id, opt)) return id;
        }
        return '';
      }
      function readSelectedPlan() {
        const toggles = seal.querySelectorAll('input[type="radio"], input[type="checkbox"]');
        let sawVisibleToggle = false;
        for (const el of toggles) {
          if (!isVisible(el)) continue;
          sawVisibleToggle = true;
          if (!el.checked) continue;
          const id = planIdFrom(el);
          /* Toggle without its own plan id: a subscribe toggle keeps the plan
             in its container's frequency select; a one-time toggle has none. */
          if (!isPlanId(id)) return planFromOwnSelect(el);
          if (isExcluded(id, el)) continue;              // bundle-only plan
          return planFromOwnSelect(el) || id;            // interval refines the group id
        }
        if (sawVisibleToggle) return '';                 // toggles exist; none picked a plan
        /* Dropdown-only widget (no toggles at all): a visible select IS the choice. */
        for (const sel of seal.querySelectorAll('select')) {
          if (!isVisible(sel) || sel.selectedIndex < 0) continue;
          const opt = sel.options[sel.selectedIndex];
          const id = String(opt.getAttribute('data-selling-plan-id') || opt.value || '').trim();
          if (isPlanId(id) && !isExcluded(id, opt)) return id;
        }
        return '';
      }

      /* Coalesce Seal's mutation bursts into one read — the widget re-renders
         aggressively and each read forces layout (offsetParent). setTimeout,
         NOT requestAnimationFrame: rAF never fires in hidden/background tabs,
         which would leave the input stale until the tab is refocused. */
      let syncTimer = 0;
      const sync = () => {
        if (syncTimer) return;
        syncTimer = setTimeout(() => {
          syncTimer = 0;
          planInput.value = readSelectedPlan();
        }, 16);
      };

      /* React to user selection AND to Seal re-rendering its widget async. */
      seal.addEventListener('change', sync);
      seal.addEventListener('click', sync);
      new MutationObserver(sync).observe(seal, {
        childList: true, subtree: true, attributes: true
      });
      sync();

      /* Authoritative re-read at submit time. Form-level (target) listeners run
         before the document-level add-to-cart interceptor, so the value the
         interceptor ships is NEVER a stale frame behind the widget. */
      form.addEventListener('submit', () => { planInput.value = readSelectedPlan(); });
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
     EXPRESS CHECKOUT GUARD
     The product pages render Shopify's dynamic checkout button so a buyer can
     skip the cart entirely. Shopify picks the wallet per device (Apple Pay /
     Google Pay / Shop Pay / PayPal) — we only decide WHETHER to offer it.

     It is hidden the moment a subscription plan is selected. Dynamic checkout
     and Seal do not reliably agree on the selling_plan field, and the failure
     mode is a subscriber checking out ONE-TIME at the wrong price — the exact
     class of bug documented in initSealFormBridge below. Subscribers take the
     Add to Bag path, which already ships the plan correctly.

     Reads the selling_plan input straight from the form rather than trusting
     the Seal bridge to have attached, so a late-rendering Seal widget can never
     leave the express button showing over a subscription selection.
  ══════════════════════════════════════════════════════════════════════ */
  function initExpressCheckoutGuard() {
    /* [data-hide-on-subscription] joined this selector for the pairing offers:
       their quoted totals are one-time arithmetic, so showing them over a
       selected subscription would advertise a price checkout would not charge —
       the same failure this guard already exists to prevent. */
    document.querySelectorAll('[data-express-checkout], [data-hide-on-subscription]').forEach(wrap => {
      const form = wrap.closest('form');
      if (!form) return;
      const scope = form.closest('[data-product-section]') || form;

      let last = null, timer = 0;

      function apply() {
        const plan = form.querySelector('[name="selling_plan"]');
        const active = !!(plan && !plan.disabled && String(plan.value || '').trim() !== '');
        /* Idempotent: writing `hidden` mutates the observed subtree, so bailing
           on an unchanged value is what stops this re-entering itself. */
        if (active === last) return;
        last = active;
        wrap.hidden = active;
      }

      /* Seal re-renders its widget aggressively; coalesce the burst. */
      function sync() {
        if (timer) return;
        timer = setTimeout(() => { timer = 0; apply(); }, 16);
      }

      apply();
      scope.addEventListener('change', sync);
      scope.addEventListener('click', sync);
      new MutationObserver(sync).observe(scope, {
        childList: true, subtree: true, attributes: true
      });
    });
  }

  function boot() {
    initMobileNav();
    initAccordions();
    initCart();
    initSealFormBridge();
    initExpressCheckoutGuard();
    initPairOffers();

    /* Boot-time sweep for the bundle-container guard: a cart poisoned via a
       direct /cart/add URL is cleaned on the next page view, not only when
       the drawer is opened. Badge/UI are re-synced only when a line was
       actually removed, so normal page loads change nothing visible. */
    fetchCart()
      .then(cart => {
        const hasRogue = (cart.items || []).some(
          item => BUNDLE_CONTAINER_HANDLES.includes(item.handle)
        );
        if (!hasRogue) return;
        return purgeBundleContainerLines(cart).then(next => applyCart(next, true));
      })
      .catch(() => { /* network hiccup — nothing to clean this view */ });
  }

  /* Expose for optional external use */
  window.AlgaeTheme = {
    addToCart, openCart, closeCart, renderCart, fetchCart,
    /* Shared currency formatter — kept so any section can print money through
       the same implementation the drawer uses. The bundle math/masking that
       once lived here was removed with the custom bundle engine. */
    formatMoney
  };
})();


/* ══════════════════════════════════════════════════════════════════════════
   BUNDLE INTEGRITY GUARD  (Bug 2 fix)
   The 33% bundle tier is a Seal selling-plan discount that a line item keeps
   even after its bundle partner is removed from the cart — there is no native
   Shopify mechanism that revokes it. This watches every cart mutation (drawer
   OR /cart page, via a fetch wrapper); if a bundle-linked line loses its
   required pair, it strips the bundle selling_plan + bundle properties so the
   single item can't reach checkout at the bundle rate, and alerts the shopper.

   SAFETY: this is INERT until the CONFIG below is filled in from a real cart.
   With no matching signal it detects no bundle lines and does nothing. The
   active rule (a complete bundle needs ≥2 line items) cannot false-positive on
   a legitimate bundle, because a valid 1-Nutrient + 1-Oil bundle is always ≥2
   distinct lines. (Tier-awareness is an optional later refinement.)

   NOTE: storefront mitigation only — bypassable with JS off. The un-bypassable
   fix is a server-side Cart Checkout Validation Function (see project notes).
══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* OFF since 2026-10-22. The ritual page no longer uses the Bundler widget
     (it was the only thing that stamped _bundler_id), and the 33% bundle plan
     lives only on the unpurchasable $0 host product, so there is nothing left
     to guard. Left on, it cost a /cart.js request on every page view and
     after every cart change. Flip to true if a Bundler bundle widget returns. */
  const GUARD_ENABLED = false;
  if (!GUARD_ENABLED) return;

  /* ===== CONFIG — confirmed from a live /cart.js (2026-06-16) ================ */
  // Property key the Bundler app stamps to mark bundle membership. Lines sharing
  // the SAME value belong to the same bundle definition.
  const BUNDLE_ID_PROP_KEYS = ['_bundler_id'];

  // Bundler also stamps the tier each line sits in. A complete bundle must span
  // BOTH sections (0 = Direct Nutrient, 1 = Oil).
  const BUNDLE_SECTION_KEY = '_bundle_section_id';

  // Subscription selling-plan IDs that grant the bundle 33% renewal tier. A
  // bundle line carrying one of these is the exploitable subscription line.
  const BUNDLE_SELLING_PLAN_IDS = [7796687088];

  // null → reset orphan to ONE-TIME. Or set the 11% standalone plan id to
  // downgrade the orphan to the standalone subscription instead.
  const STANDALONE_PLAN_ID = null;
  /* =========================================================================== */

  const CART_MUTATION = /\/cart\/(change|add|update|clear)(\.js)?(\?|$)/;
  let busy = false;
  const handled = new Set();   // guard against reprocessing in a loop

  function bundleIdOf(line) {
    const p = line.properties || {};
    for (const k of BUNDLE_ID_PROP_KEYS) if (p[k]) return String(p[k]);
    return null;
  }
  function planIdOf(line) {
    const a = line.selling_plan_allocation;
    return a && a.selling_plan ? String(a.selling_plan.id) : null;
  }
  function isBundleLine(line) { return !!bundleIdOf(line); }
  function isBundlePlanLine(line) {
    const planId = planIdOf(line);
    return !!planId && BUNDLE_SELLING_PLAN_IDS.map(String).indexOf(planId) !== -1;
  }
  function sectionOf(line) {
    const p = line.properties || {};
    return (p[BUNDLE_SECTION_KEY] != null) ? String(p[BUNDLE_SECTION_KEY]) : null;
  }
  /* Complete bundle = both tiers present (Nutrient + Oil). Section info is
     authoritative when stamped; fall back to a ≥2-line count if it's missing. */
  function groupIsValid(lines) {
    const sections = lines.map(sectionOf).filter(s => s !== null);
    if (sections.length) return new Set(sections).size >= 2;
    return lines.length >= 2;
  }
  function isBundleProp(key) {
    return key.indexOf('_bundler') === 0 ||
           key.indexOf('_bundle_') === 0 ||
           BUNDLE_ID_PROP_KEYS.indexOf(key) !== -1;
  }

  async function neutralize(line) {
    const cleaned = {};
    Object.keys(line.properties || {}).forEach(k => {
      if (!isBundleProp(k)) cleaned[k] = line.properties[k];
    });
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: line.key,
        quantity: line.quantity,
        selling_plan: STANDALONE_PLAN_ID,  // null → one-time
        properties: cleaned
      })
    });
  }

  function notifyBroken() {
    const el = document.getElementById('cart-error');
    const msg = 'Your bundle is incomplete, so its subscription pricing was removed ' +
                'from the leftover item. Add a Nutrient and an Oil to restore bundle savings.';
    if (el) { el.textContent = msg; el.hidden = false; setTimeout(() => (el.hidden = true), 6000); }
    else { try { window.alert(msg); } catch (_) {} }
  }

  async function enforce() {
    if (busy) return;
    busy = true;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const bundleLines = cart.items.filter(isBundleLine);
      if (!bundleLines.length) { handled.clear(); return; }

      const groups = {};
      bundleLines.forEach(l => {
        const key = bundleIdOf(l) || '__implicit__';
        (groups[key] = groups[key] || []).push(l);
      });

      /* Only SUBSCRIPTION lines in a broken bundle are worth fixing — they carry
         the 33% renewal tier. (A one-time orphan's 22% comes from a product-level
         automatic discount that storefront JS can't revoke; see project notes.) */
      const orphans = [];
      Object.keys(groups).forEach(key => {
        if (!groupIsValid(groups[key])) {
          groups[key].forEach(l => { if (isBundlePlanLine(l)) orphans.push(l); });
        }
      });

      const todo = orphans.filter(l => !handled.has(l.key));
      if (todo.length) {
        for (const line of todo) { handled.add(line.key); await neutralize(line); }
        notifyBroken();
        const onCartPage = /^\/cart(\/|$|\?)/.test(window.location.pathname);
        if (onCartPage) { window.location.reload(); return; }
        if (window.AlgaeTheme && window.AlgaeTheme.renderCart) window.AlgaeTheme.renderCart();
      }
    } catch (_) {
      /* Fail OPEN — never dead-end a paying customer on a network hiccup. */
    } finally {
      busy = false;
    }
  }

  /* Observe every cart mutation regardless of which UI triggered it. */
  const _fetch = window.fetch;
  window.fetch = function (input, init) {
    const url = (typeof input === 'string') ? input : (input && input.url) || '';
    const out = _fetch.apply(this, arguments);
    if (CART_MUTATION.test(url)) out.then(() => setTimeout(enforce, 60)).catch(() => {});
    return out;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforce);
  } else {
    enforce();
  }
})();
